import { Navigate, Outlet, useLocation } from "react-router-dom";
import { usePermissions } from "../../contexts/PermissionContext";
import { useStateContext } from "../../contexts/ContextProvider";
import { getRequiredPermission, defaultRouteOrder, routePermissions } from "../../config/routePermissions";

/**
 * Composant de garde qui vérifie les permissions pour les routes.
 * - Attend que l'utilisateur soit chargé avant de vérifier
 * - Super admin a accès à tout
 * - Routes avec permission null sont réservées aux super-admins
 * - Redirige vers la première route accessible si pas de permission
 */
export default function RequirePermission() {
    const location = useLocation();
    const { hasPermission, hasAnyPermission, isSuperAdmin, isGroupAdmin } = usePermissions();
    const { isUserLoading, user } = useStateContext();

    // Attendre que l'utilisateur soit chargé avant de vérifier les permissions
    if (isUserLoading || user.id === 0) {
        return null; // On ne rend rien pendant le chargement
    }

    // Super admin ou admin de groupe a accès à tout (sauf routes super-admin only)
    if (isSuperAdmin || isGroupAdmin) {
        // Vérifier si c'est une route réservée aux super-admins
        const requiredPerm = getRequiredPermission(location.pathname);
        if (requiredPerm === null && !isSuperAdmin) {
            // Route super-admin only, mais l'utilisateur est juste admin de groupe
            return <Navigate to={getFirstAccessibleRoute()} replace />;
        }
        return <Outlet />;
    }

    // Trouver la permission requise pour cette route
    const requiredPermission = getRequiredPermission(location.pathname);

    // Route réservée aux super-admins (permission === null)
    if (requiredPermission === null) {
        return <Navigate to={getFirstAccessibleRoute()} replace />;
    }

    // Vérifier si l'utilisateur a la permission requise
    if (requiredPermission !== undefined) {
        const hasAccess = checkPermission(requiredPermission);
        if (!hasAccess) {
            return <Navigate to={getFirstAccessibleRoute()} replace />;
        }
    }

    // Permission accordée ou pas de permission requise
    return <Outlet />;

    /**
     * Vérifie si l'utilisateur a la permission (simple ou multiple)
     */
    function checkPermission(perm: string | string[]): boolean {
        if (Array.isArray(perm)) {
            return hasAnyPermission(perm);
        }
        return hasPermission(perm);
    }

    /**
     * Trouve la première route accessible pour l'utilisateur
     */
    function getFirstAccessibleRoute(): string {
        for (const route of defaultRouteOrder) {
            const perm = routePermissions[route];

            // Pas de permission requise pour cette route
            if (perm === undefined) {
                return route;
            }

            // Route super-admin only, skip
            if (perm === null) {
                continue;
            }

            // Vérifier si l'utilisateur a la permission
            if (checkPermission(perm)) {
                return route;
            }
        }

        // Fallback vers settings (devrait toujours être accessible)
        return '/settings';
    }
}
